import React, { useMemo, useState } from 'react';
import LeadAuthScreen from './LeadAuthScreen';
import GroupSelectionScreen from './GroupSelectionScreen';
import GroupDashboard from './GroupDashboard';
import EventSelectionScreen from './EventSelectionScreen';
import GroupFinanceScreen from './GroupFinanceScreen';

import { useLeadAttendance } from '../../context/LeadAttendanceContext';
import type { AttendanceInput, MeetingLocation } from '../../context/LeadAttendanceContext';

export default function LeadAttendanceApp() {
  const {
    apiUrl,
    isBootstrapping,
    isAuthenticating,
    isLoadingParticipants,
    sessionToken,
    lead,
    groups,
    selectedGroup,
    selectedGroupId,
    isLoadingEvents,
    hasMoreEvents,
    eventsSearch,
    eventsStatusFilter,
    groupInsights,
    isLoadingInsights,
    events,
    selectedEventId,
    selectedEvent,
    selectEvent,
    createEvent,
    loadMoreEvents,
    setEventFilters,
    refreshGroupInsights,
    getParticipantInsights,
    participants,
    authLead,
    selectGroup,
    clearSelectedGroup,
    refreshParticipants,
    recordAttendance,
    getGroupHistory,
    logout,
  } = useLeadAttendance();

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmittingAttendance, setIsSubmittingAttendance] = useState(false);
  const [activeMainView, setActiveMainView] = useState<'events' | 'finance'>('events');

  const attendanceHistory = useMemo(() => getGroupHistory(selectedGroupId), [getGroupHistory, selectedGroupId]);

  const handleAuth = async (leadCode: string) => {
    setError(null);
    setNotice(null);

    try {
      await authLead(leadCode);
    } catch (authError) {
      const message = authError instanceof Error ? authError.message : 'Failed to authenticate';
      setError(message);
    }
  };

  const handleSelectGroup = async (groupId: string) => {
    setError(null);
    setNotice(null);
    setActiveMainView('events');

    try {
      await selectGroup(groupId);
    } catch (groupError) {
      const message = groupError instanceof Error ? groupError.message : 'Failed to select group';
      setError(message);
    }
  };

  const handleRecordAttendance = async (payload: {
    date: string;
    generalNotes: string;
    location?: MeetingLocation;
    attendances: AttendanceInput[];
  }) => {
    setIsSubmittingAttendance(true);
    setError(null);
    setNotice(null);

    try {
      await recordAttendance({
        ...payload,
        eventId: selectedEventId || undefined,
      });
      setNotice('Attendance saved successfully.');
    } catch (recordError) {
      const message = recordError instanceof Error ? recordError.message : 'Failed to record attendance';
      setError(message);
      throw recordError;
    } finally {
      setIsSubmittingAttendance(false);
    }
  };

  if (isBootstrapping) {
    return (
      <section className="min-h-screen bg-slate-50 px-4 py-10">
        <div className="mx-auto w-full max-w-md animate-pulse rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          <div className="h-5 w-48 rounded bg-slate-200" />
          <div className="mt-2 h-4 w-64 rounded bg-slate-100" />
          <div className="mt-6 h-12 rounded-xl bg-slate-100" />
          <div className="mt-3 h-12 rounded-xl bg-slate-100" />
        </div>
      </section>
    );
  }

  if (!sessionToken || !lead || groups.length === 0) {
    return <LeadAuthScreen isAuthenticating={isAuthenticating} onSubmit={handleAuth} error={error} />;
  }

  if (!selectedGroup || (groups.length > 1 && !selectedGroupId)) {
    return (
      <GroupSelectionScreen
        groups={groups}
        leadName={lead.name}
        onSelectGroup={handleSelectGroup}
        onChangeLeadCode={logout}
        error={error}
      />
    );
  }

  if (!selectedEventId) {
    if (activeMainView === 'finance' && selectedGroup?.allowFinance) {
      return (
        <GroupFinanceScreen
          groupName={selectedGroup.name}
          onBack={() => setActiveMainView('events')}
          onLogout={logout}
        />
      );
    }

    return (
      <EventSelectionScreen
        events={events}
        groupName={selectedGroup.name}
        leadName={lead.name}
        allowFinance={Boolean(selectedGroup.allowFinance)}
        isLoading={isLoadingEvents}
        hasMore={hasMoreEvents}
        search={eventsSearch}
        statusFilter={eventsStatusFilter}
        insights={groupInsights}
        isLoadingInsights={isLoadingInsights}
        participants={participants}
        participantsCount={participants.length}
        attendanceHistory={attendanceHistory}
        onSelectEvent={selectEvent}
        onCreateEvent={createEvent}
        onLoadMore={loadMoreEvents}
        onFilterChange={setEventFilters}
        onRefreshInsights={refreshGroupInsights}
        onLoadParticipantInsights={getParticipantInsights}
        onOpenFinance={() => setActiveMainView('finance')}
        onBack={clearSelectedGroup}
        onLogout={logout}
      />
    );
  }

  return (
    <GroupDashboard
      apiUrl={apiUrl}
      sessionToken={sessionToken}
      lead={lead}
      group={selectedGroup}
      event={selectedEvent}
      hasMultipleGroups={groups.length > 1}
      participants={participants}
      isLoadingParticipants={isLoadingParticipants}
      isSubmittingAttendance={isSubmittingAttendance}
      attendanceHistory={attendanceHistory}
      error={error}
      notice={notice}
      onSwitchGroup={() => selectEvent(null)}
      onRefreshParticipants={refreshParticipants}
      onLogout={logout}
      onRecordAttendance={handleRecordAttendance}
    />
  );
}
